"use client";

/**
 * TaskAccessLevelPicker — the composer/task-header placement of the Assistant
 * Access dial (permissions v2). Two modes, chosen by whether the task exists yet:
 *
 *   committed task  → shows the task's EFFECTIVE level (override ?? user
 *     default), a "(default)" marker while inheriting; picking writes the
 *     per-task override (setTaskAccessLevel → SET_ACCESS_LEVEL socket event).
 *
 *   new-task composer (no task yet) → the task hasn't been created, so there is
 *     nothing to write to (setTaskAccessLevel no-ops on an absent task). The
 *     picker binds to the composer DRAFT (nextTaskAccessLevel) instead; the
 *     draft is consumed at task creation and persisted on send-ack. This is why
 *     the control now appears on the new-task screen, not only after the first
 *     message.
 *
 * While inheriting, a customized user default (non-null accessConfig) renders as
 * Custom — display only; per-task config editing is out of B2 scope.
 *
 * The inherited default is only shown once it has actually been read. It used to
 * be seeded with 'relaxed', so a Careful user's chip claimed Relaxed on first
 * paint and kept claiming it if the read failed. Unknown now stays unknown: the
 * chip says so and offers Try again. An explicit override still wins, so this
 * only ever shows where there is nothing else to show.
 */

import { useEffect, useState } from "react";
import type { AccessLevel } from "@bash/shared";
import { useTaskStore } from "@/stores/taskStore";
import { getUserAccessSettingsCached } from "@/lib/api/access-level";
import { AccessLevelPicker } from "./AccessLevelPicker";

interface Props {
  /** The active task, or null on the new-task composer (draft mode). */
  taskId: string | null;
  /** Icon-only trigger for a squeezed composer — see AccessLevelPicker. */
  compact?: boolean;
  className?: string;
}

export function TaskAccessLevelPicker({ taskId, compact, className }: Props) {
  // A task is "committed" once it exists in the store (created on send). Before
  // that — taskId null, or a pending id that has no task row yet — we are in
  // draft mode and bind to the composer draft.
  const committed = useTaskStore((s) =>
    taskId != null ? s.tasks.has(taskId) : false
  );
  const taskOverride = useTaskStore(
    (s) => (taskId != null ? s.tasks.get(taskId)?.accessLevel : null) ?? null
  );
  const draftLevel = useTaskStore((s) => s.nextTaskAccessLevel);
  const setTaskAccessLevel = useTaskStore((s) => s.setTaskAccessLevel);
  const setNextTaskAccessLevel = useTaskStore((s) => s.setNextTaskAccessLevel);

  const [userDefault, setUserDefault] = useState<AccessLevel | null>(null);
  const [userCustomized, setUserCustomized] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    let alive = true;
    getUserAccessSettingsCached()
      .then((settings) => {
        if (!alive) return;
        setUserDefault(settings.accessLevel);
        setUserCustomized(settings.accessConfig != null);
        setLoadFailed(false);
      })
      .catch(() => {
        if (alive) setLoadFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [reloads]);

  const override = committed ? taskOverride : draftLevel;
  const inheriting = override == null;
  // null while the default is unread: the picker renders that as unknown rather
  // than naming a level. An override always resolves it.
  const effective = override ?? userDefault;

  const onChange = (level: AccessLevel) => {
    if (committed && taskId != null) setTaskAccessLevel(taskId, level);
    else setNextTaskAccessLevel(level);
  };

  return (
    <AccessLevelPicker
      value={effective}
      loadFailed={loadFailed}
      onRetry={() => {
        setLoadFailed(false);
        setReloads((n) => n + 1);
      }}
      inheriting={inheriting}
      custom={inheriting && userCustomized}
      onChange={onChange}
      compact={compact}
      collapseOnMobile
      className={className}
    />
  );
}
